import { createContext, useCallback, useContext, useEffect, useState } from "react";

const TR = {
  en: {
    dashboard: "Operations Dashboard",
    company_profile: "Company Profile",
    company_config: "Company Configuration",
    users: "Users Management",
    roles: "Roles & Permissions",
    approvals: "Approvals",
    audit: "Audit Log",
    platform: "Platform Console",
    language: "Language",
    signout: "Sign out",
    active_company: "Active Company",
    internal_ops: "Internal Operations",
    governance: "Governance",
    // ---- Public landing page (customer-facing) ----
    l_signin: "Sign In",
    l_trial_cta: "Start 14-Day Free Trial",
    l_hero_badge: "For Travel Agencies · Tour Operators · DMCs",
    l_hero_title_a: "One operating system for your ",
    l_hero_title_b: "entire travel business",
    l_hero_tagline: "Rock-solid integrated operating system",
    l_hero_sub: "Built on a strong foundation. Designed to integrate intelligence and process — connecting inquiry, costing, quotation, booking, invoicing, payment and trip operations in a single workflow.",
    l_hero_motto: "Build · Connect · Grow · Beyond",
    l_hero_ai_badge: "AI proposes. Human decides.",
    l_hero_ai_badge_sub: "Controlled, auditable AI assistance",
    l_prob_kicker: "Why PETRINOS",
    l_prob_title: "Fragmented processes, one operating system",
    l_prob_sub: "PETRINOS organizes and integrates your travel-business operations into a single structured workflow — from first inquiry to completed trip.",
    l_p1_t: "Scattered inquiries",
    l_p1_d: "Leads arrive via WhatsApp, email and spreadsheets — nothing is tracked end-to-end.",
    l_p2_t: "Pricing & margin mistakes",
    l_p2_d: "Manual costing across currencies leads to wrong quotes and invisible margins.",
    l_p3_t: "No operational continuity",
    l_p3_d: "Quotation, booking and trip operations live in disconnected tools.",
    l_wf_kicker: "Core Workflow",
    l_wf_title: "Eleven stages. One continuous operation.",
    l_wf_sub: "Continuity across the entire travel operations lifecycle — each stage feeds the next, nothing falls through.",
    l_st1: "Inquiry", l_st2: "Qualification", l_st3: "Costing", l_st4: "Quotation",
    l_st5: "Revision", l_st6: "Acceptance", l_st7: "Booking", l_st8: "Invoice",
    l_st9: "Payment", l_st10: "Trip Operations", l_st11: "Completion",
    l_ai_kicker: "Artificial Intelligence",
    l_ai_title: "AI proposes",
    l_ai_desc: "PETRINOS uses AI-assisted drafting to structure unstructured customer messages into reviewable inquiry drafts, and to surface proposals and recommendations across your workflow.",
    l_ai_b1: "AI-assisted inquiry drafting",
    l_ai_b2: "AI-proposed process suggestions",
    l_ai_b3: "AI-assisted document preparation",
    l_ai_b4: "AI-informed business insights",
    l_hi_kicker: "Human Intelligence",
    l_hi_title: "Human decides",
    l_hi_desc: "Nothing is committed autonomously. Every AI proposal is reviewed and confirmed by your team, with maker-checker approvals and a complete audit trail behind each decision.",
    l_hi_b1: "Human confirmation before any record is saved",
    l_hi_b2: "Maker-checker approval chain",
    l_hi_b3: "Immutable audit log of every action",
    l_hi_b4: "Role-based access by organization",
    l_ai_banner_a: "Human Intelligence + Artificial Intelligence — ",
    l_ai_banner_b: "controlled, auditable, always human-led.",
    l_cap_kicker: "One Connected System",
    l_cap_title: "Everything your travel business needs. Connected in one system.",
    l_cap_sub: "From the first inquiry to the completed trip, PETRINOS connects the people, data, suppliers, documents, money and intelligence behind your operation.",
    l_c1_t: "CUSTOMER & CRM",
    l_c1_d: "Keep every customer, traveler, inquiry and interaction connected throughout the entire relationship.",
    l_c2_t: "INQUIRY & SALES",
    l_c2_d: "Capture inquiries, qualify opportunities, manage requirements, follow up and move prospects toward acceptance.",
    l_c3_t: "ITINERARY & PRODUCT BUILDER",
    l_c3_d: "Build itineraries and travel products from real services, suppliers, rates and customer requirements.",
    l_c4_t: "COSTING & PRICING",
    l_c4_d: "Calculate supplier costs, multi-currency pricing, markup and margin with a consistent commercial engine.",
    l_c5_t: "QUOTATIONS & DOCUMENTS",
    l_c5_d: "Create professional quotations, revisions, proforma invoices, invoices, receipts and operational documents from connected data.",
    l_c6_t: "BOOKINGS & OPERATIONS",
    l_c6_d: "Turn accepted quotations into bookings and coordinate suppliers, passengers, services, documents and trip execution.",
    l_c7_t: "PAYMENTS & FINANCIAL CONTROL",
    l_c7_d: "Track receivables, supplier payables, payment schedules, settlements and the financial status of each booking.",
    l_c8_t: "SUPPLIER MANAGEMENT",
    l_c8_d: "Manage your own supplier network, contracts, rates, services and payment terms alongside connected supplier platforms.",
    l_c9_t: "AI INTELLIGENCE",
    l_c9_d: "Use your company's authorized data and connected services to assist with itinerary creation, costing, quotations, follow-up and business insights.",
    l_c10_t: "GOVERNANCE & AUDIT",
    l_c10_d: "Keep AI and users accountable with permissions, approval controls, maker-checker workflows and complete audit trails.",
    l_aud_kicker: "Built For",
    l_aud_title: "Travel businesses at every stage",
    l_aud_sub: "Whether you are starting a travel business, growing an established operation, running tours, or managing destinations, PETRINOS gives you one connected operating system for the work behind every booking.",
    l_a1_t: "START-UP TRAVEL AGENCIES",
    l_a1_d: "Build structured operations from day one — without creating a maze of spreadsheets, chats and disconnected tools.",
    l_a2_t: "GROWING TRAVEL BUSINESSES",
    l_a2_d: "Connect inquiries, costing, quotations, bookings, payments and operations as your business becomes more complex.",
    l_a3_t: "TOUR OPERATORS",
    l_a3_d: "Standardize products, itineraries, costing, suppliers and trip operations across every departure.",
    l_a4_t: "DMCs",
    l_a4_d: "Coordinate customers, suppliers, quotations and on-ground operations in one continuous workflow.",
    l_cta_title: "Start your 14-day free trial",
    l_cta_sub: "Run your travel business on PETRINOS Travel OS. Free for 14 days — then first-year promotional pricing, then the standard rate. No charge today.",
    l_footer_left: "PETRINOS Travel OS — Travel Operating System for Travel Agencies, Tour Operators & DMCs",
    l_footer_right: "Operated by HelStèEn · PT Helsteen Syntra Teknologi · People · Places · Possibilities",
  },
  id: {
    dashboard: "Dasbor Operasi",
    company_profile: "Profil Perusahaan",
    company_config: "Konfigurasi Perusahaan",
    users: "Manajemen Pengguna",
    roles: "Peran & Izin",
    approvals: "Persetujuan",
    audit: "Log Audit",
    platform: "Konsol Platform",
    language: "Bahasa",
    signout: "Keluar",
    active_company: "Perusahaan Aktif",
    internal_ops: "Operasi Internal",
    governance: "Tata Kelola",
    // ---- Halaman publik (landing) ----
    l_signin: "Masuk",
    l_trial_cta: "Mulai Uji Coba Gratis 14 Hari",
    l_hero_badge: "Untuk Agen Perjalanan · Tour Operator · DMC",
    l_hero_title_a: "Satu sistem operasi untuk ",
    l_hero_title_b: "seluruh bisnis travel Anda",
    l_hero_tagline: "Sistem operasi terintegrasi yang kokoh",
    l_hero_sub: "Dibangun di atas fondasi yang kuat. Dirancang untuk mengintegrasikan kecerdasan dan proses — menghubungkan inquiry, costing, penawaran, booking, invoice, pembayaran, dan operasional perjalanan dalam satu alur kerja.",
    l_hero_motto: "Bangun · Terhubung · Bertumbuh · Melampaui",
    l_hero_ai_badge: "AI mengusulkan. Manusia memutuskan.",
    l_hero_ai_badge_sub: "Bantuan AI yang terkendali dan teraudit",
    l_prob_kicker: "Mengapa PETRINOS",
    l_prob_title: "Proses yang terfragmentasi, satu sistem operasi",
    l_prob_sub: "PETRINOS mengorganisasi dan mengintegrasikan operasional bisnis travel Anda ke dalam satu alur kerja terstruktur — dari inquiry pertama hingga perjalanan selesai.",
    l_p1_t: "Inquiry yang tersebar",
    l_p1_d: "Prospek datang melalui WhatsApp, email, dan spreadsheet — tidak ada yang terlacak dari awal hingga akhir.",
    l_p2_t: "Kesalahan harga & margin",
    l_p2_d: "Costing manual lintas mata uang menyebabkan penawaran yang keliru dan margin yang tak terlihat.",
    l_p3_t: "Tidak ada kesinambungan operasional",
    l_p3_d: "Penawaran, booking, dan operasional perjalanan berjalan di tools yang terpisah-pisah.",
    l_wf_kicker: "Alur Kerja Inti",
    l_wf_title: "Sebelas tahap. Satu operasi yang berkesinambungan.",
    l_wf_sub: "Kesinambungan di seluruh siklus operasional travel — setiap tahap mengalir ke tahap berikutnya, tidak ada yang terlewat.",
    l_st1: "Inquiry", l_st2: "Kualifikasi", l_st3: "Costing", l_st4: "Penawaran",
    l_st5: "Revisi", l_st6: "Persetujuan", l_st7: "Booking", l_st8: "Invoice",
    l_st9: "Pembayaran", l_st10: "Operasional Perjalanan", l_st11: "Penyelesaian",
    l_ai_kicker: "Kecerdasan Buatan",
    l_ai_title: "AI mengusulkan",
    l_ai_desc: "PETRINOS menggunakan penyusunan draft berbantuan AI untuk menstrukturkan pesan pelanggan yang tidak terstruktur menjadi draft inquiry yang dapat ditinjau, serta menampilkan usulan dan rekomendasi di seluruh alur kerja Anda.",
    l_ai_b1: "Penyusunan draft inquiry berbantuan AI",
    l_ai_b2: "Usulan proses dari AI",
    l_ai_b3: "Persiapan dokumen berbantuan AI",
    l_ai_b4: "Wawasan bisnis berbasis AI",
    l_hi_kicker: "Kecerdasan Manusia",
    l_hi_title: "Manusia memutuskan",
    l_hi_desc: "Tidak ada yang dikomit secara otomatis. Setiap usulan AI ditinjau dan dikonfirmasi oleh tim Anda, dengan persetujuan maker-checker dan jejak audit lengkap di balik setiap keputusan.",
    l_hi_b1: "Konfirmasi manusia sebelum data tersimpan",
    l_hi_b2: "Rantai persetujuan maker-checker",
    l_hi_b3: "Log audit yang tidak dapat diubah untuk setiap aksi",
    l_hi_b4: "Akses berbasis peran per organisasi",
    l_ai_banner_a: "Kecerdasan Manusia + Kecerdasan Buatan — ",
    l_ai_banner_b: "terkendali, teraudit, selalu dipimpin manusia.",
    l_cap_kicker: "Satu Sistem Terhubung",
    l_cap_title: "Semua yang dibutuhkan bisnis travel Anda. Terhubung dalam satu sistem.",
    l_cap_sub: "Dari inquiry pertama hingga perjalanan selesai, PETRINOS menghubungkan orang, data, supplier, dokumen, pembayaran, dan kecerdasan yang menjalankan operasional bisnis Anda.",
    l_c1_t: "PELANGGAN & CRM",
    l_c1_d: "Simpan setiap pelanggan, traveler, inquiry, dan interaksi yang saling terhubung sepanjang hubungan bisnis.",
    l_c2_t: "INQUIRY & PENJUALAN",
    l_c2_d: "Tangkap inquiry, kualifikasi peluang, kelola kebutuhan, lakukan follow-up, dan arahkan prospek menuju persetujuan.",
    l_c3_t: "PENYUSUN ITINERARY & PRODUK",
    l_c3_d: "Bangun itinerary dan produk travel dari layanan, supplier, tarif, dan kebutuhan pelanggan yang nyata.",
    l_c4_t: "COSTING & HARGA",
    l_c4_d: "Hitung biaya supplier, harga multi-mata uang, markup, dan margin dengan mesin komersial yang konsisten.",
    l_c5_t: "PENAWARAN & DOKUMEN",
    l_c5_d: "Buat penawaran profesional, revisi, proforma invoice, invoice, kwitansi, dan dokumen operasional dari data yang terhubung.",
    l_c6_t: "BOOKING & OPERASIONAL",
    l_c6_d: "Ubah penawaran yang disetujui menjadi booking dan koordinasikan supplier, penumpang, layanan, dokumen, dan pelaksanaan perjalanan.",
    l_c7_t: "PEMBAYARAN & KONTROL KEUANGAN",
    l_c7_d: "Lacak piutang, utang supplier, jadwal pembayaran, settlement, dan status keuangan setiap booking.",
    l_c8_t: "MANAJEMEN SUPPLIER",
    l_c8_d: "Kelola jaringan supplier Anda sendiri — kontrak, tarif, layanan, dan ketentuan pembayaran — bersama platform supplier yang terhubung.",
    l_c9_t: "KECERDASAN AI",
    l_c9_d: "Gunakan data resmi perusahaan Anda dan layanan yang terhubung untuk membantu penyusunan itinerary, costing, penawaran, follow-up, dan wawasan bisnis.",
    l_c10_t: "TATA KELOLA & AUDIT",
    l_c10_d: "Jaga akuntabilitas AI dan pengguna dengan izin, kontrol persetujuan, alur maker-checker, dan jejak audit yang lengkap.",
    l_aud_kicker: "Dibangun Untuk",
    l_aud_title: "Bisnis travel di setiap tahap",
    l_aud_sub: "Baik Anda baru memulai bisnis travel, mengembangkan operasional yang sudah berjalan, mengelola tour, maupun menjalankan DMC, PETRINOS memberikan satu sistem operasi terhubung untuk seluruh pekerjaan di balik setiap booking.",
    l_a1_t: "AGEN TRAVEL START-UP",
    l_a1_d: "Bangun operasional yang terstruktur sejak hari pertama — tanpa membuat bisnis bergantung pada spreadsheet, chat, dan berbagai tools yang terpisah.",
    l_a2_t: "BISNIS TRAVEL YANG BERKEMBANG",
    l_a2_d: "Hubungkan inquiry, costing, quotation, booking, pembayaran, dan operasional seiring bisnis Anda menjadi semakin kompleks.",
    l_a3_t: "TOUR OPERATOR",
    l_a3_d: "Standarkan produk, itinerary, costing, supplier, dan operasional perjalanan untuk setiap keberangkatan.",
    l_a4_t: "DMC",
    l_a4_d: "Koordinasikan customer, supplier, quotation, dan operasional di lapangan dalam satu alur kerja yang berkesinambungan.",
    l_cta_title: "Mulai uji coba gratis 14 hari Anda",
    l_cta_sub: "Jalankan bisnis travel Anda dengan PETRINOS Travel OS. Gratis 14 hari — kemudian harga promosi tahun pertama, lalu tarif standar. Tanpa biaya hari ini.",
    l_footer_left: "PETRINOS Travel OS — Travel Operating System untuk Agen Perjalanan, Tour Operator & DMC",
    l_footer_right: "Dioperasikan oleh HelStèEn · PT Helsteen Syntra Teknologi · People · Places · Possibilities",
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("tf-lang") || "en");
  useEffect(() => {
    localStorage.setItem("tf-lang", lang);
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);
  const t = useCallback((k) => (TR[lang] && TR[lang][k]) || TR.en[k] || k, [lang]);
  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const c = useContext(I18nContext);
  if (!c) throw new Error("useI18n must be used within I18nProvider");
  return c;
}
